import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import type { StuffInRewardBoxType } from '@sorare/core/src/components/rewards/RewardBoxDialog/types';
import { RewardCount } from '@sorare/core/src/components/rewards/RewardCount';
import { isType } from '@sorare/core/src/lib/gql';
import { lazy } from '@sorare/core/src/lib/retry';

import type {
  ShopItemInRewardBox_ProbabilisticBundleSlotItemInterface,
  ShopItemInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotRewardShopItem_,
} from './__generated__/index.graphql';

const ModelPreloader = lazy(
  async () => import('@sorare/trois/src/atoms/preloaders/ModelPreloader'),
  'ModelPreloader'
);
const ShopItemInRewardBoxTrois = lazy(
  async () =>
    import('@sorare/trois/src/components/RewardBox/ShopItemInRewardBox'),
  'ShopItemInRewardBox'
);

const NoWrap = styled.div`
  white-space: nowrap;
`;

type ShopItemInRewardBoxPreloaderProps = {
  item: ShopItemInRewardBox_ProbabilisticBundleSlotItemInterface;
};

type Props = {
  visible: boolean;
} & ShopItemInRewardBoxPreloaderProps;

const ShopItemInRewardBoxCanHandle = (
  item: Props['item']
): item is ShopItemInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotRewardShopItem_ => {
  return isType(item, 'ProbabilisticBundleSlotRewardShopItem');
};

export const ShopItemInRewardBox: StuffInRewardBoxType<
  ShopItemInRewardBox_ProbabilisticBundleSlotItemInterface
> = ({ item, visible }: Props) => {
  if (!ShopItemInRewardBoxCanHandle(item)) {
    return null;
  }
  const { rewardShopItem } = item;
  return (
    <ShopItemInRewardBoxTrois
      visible={visible}
      url={rewardShopItem.shopItem.threeDimensionalAssetUrl}
      pictureUrl={rewardShopItem.shopItem.pictureUrl}
      quantity={rewardShopItem.quantity}
      description={
        <NoWrap>
          <RewardCount
            quantity={rewardShopItem.quantity}
            rewardName={rewardShopItem.shopItem.name}
          />
        </NoWrap>
      }
    />
  );
};

ShopItemInRewardBox.fragments = {
  ProbabilisticBundleSlotItemInterface: gql`
    fragment ShopItemInRewardBox_ProbabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
      id
      ... on ProbabilisticBundleSlotRewardShopItem {
        id
        rewardShopItem {
          quantity
          shopItem {
            id
            pictureUrl
            name
            threeDimensionalAssetUrl
          }
        }
      }
    }
  ` as TypedDocumentNode<ShopItemInRewardBox_ProbabilisticBundleSlotItemInterface>,
};

const ShopItemInRewardBoxPreloader = ({
  item,
}: ShopItemInRewardBoxPreloaderProps) => {
  if (!ShopItemInRewardBoxCanHandle(item)) {
    return null;
  }
  return (
    // FIXME
    <ModelPreloader url="https://assets.sorare.com/temp/xp-boost-limited.glb" />
  );
};

ShopItemInRewardBox.preloader = ShopItemInRewardBoxPreloader;

ShopItemInRewardBox.canHandle = ShopItemInRewardBoxCanHandle;

ShopItemInRewardBox.description = (item: Props['item']) => {
  if (!ShopItemInRewardBoxCanHandle(item)) {
    return null;
  }

  const {
    quantity,
    shopItem: { name },
  } = item.rewardShopItem;
  if (quantity > 1) {
    return `${quantity}× ${name}`;
  }
  return name;
};

ShopItemInRewardBox.fallback = (item: Props['item']) => {
  if (!ShopItemInRewardBoxCanHandle(item)) {
    return null;
  }
  return (
    <div>
      {ShopItemInRewardBox.description(item)}
      <img src={item.rewardShopItem.shopItem.pictureUrl} alt="" />
    </div>
  );
};
